import getPriceForMinDuration from "helpers/getPriceForMinDuration";
import React, { useEffect, useState, useRef, useCallback, memo } from "react";
import { GoogleMap, OverlayView } from "@react-google-maps/api";
import { darkerMapStyle } from "./darkerMapStyles";
import { ArrowRight } from "assets/icons";
import { useSelector, useDispatch } from "react-redux";
import { setFilters } from "store/filters/operations";
import { Checkbox } from "reusableComponents";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { useNavigate } from "react-router";

// Utility functions
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

const containerStyle = {
  width: "100%",
  height: "35vh",
};

const defaultCenter = { lat: 48.8575475, lng: 2.3513765 };

const mapOptions = {
  styles: darkerMapStyle,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: true,
};

// Cache for geocoding requests
const geocodeCache = new Map();

// Track map loads for monitoring
let mapLoadCount = 0;

// Google Maps script loader
const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    if (document.querySelector('script[src*="maps.googleapis"]')) {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      return;
    }

    window.initGoogleMaps = () => {
      if (window.google && window.google.maps) {
        resolve();
      } else {
        reject(new Error('Google Maps failed to load'));
      }
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
};

// Memoized marker component
const MapMarker = memo(({ marker, isActive, isHovered, onHover, onLeave, onClick }) => {
  const handleMouseEnter = useCallback(() => {
    onHover(marker.id);
  }, [onHover, marker.id]);

  const handleMouseLeave = useCallback(() => {
    onLeave();
  }, [onLeave]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onClick(marker.id);
  }, [onClick, marker.id]);

  const handleDetailsClick = useCallback((e) => {
    e.stopPropagation();
    onClick(marker.id);
  }, [onClick, marker.id]);

  return (
    <OverlayView
      position={{ lat: marker.lat, lng: marker.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        className={`${styles.priceMarker} ${
          isActive ? styles.markerActive : ""
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {isHovered && marker.details && (
          <div
            className={styles.details}
            onClick={handleDetailsClick}
          >
            {marker.details.imageUrl && (
              <img src={marker.details.imageUrl} alt={marker.details.name || "Space"} />
            )}
            {marker.details.usage && (
              <p className={styles.usage}>{marker.details.usage}</p>
            )}
            <p className={styles.name}>{marker.details.name || "Space"}</p>
            {(marker.details.postalCode || marker.details.city) && (
              <p className={styles.address}>
                {marker.details.postalCode && `${marker.details.postalCode}, `}
                {marker.details.city}
              </p>
            )}
          </div>
        )}
        <span className={styles.priceText}>{marker.price}</span>
      </div>
    </OverlayView>
  );
});

const SearchMap = ({
  spaces = [],
  activeSpace,
  setActiveSpace,
  isMapVisible,
  setIsMapVisible,
}) => {
  const mapRef = useRef();
  const mapInstanceRef = useRef(null); // Store actual map instance
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filters = useSelector((state) => state.filters);
  
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Refs for optimization
  const prevSpacesRef = useRef([]);
  const prevMarkersRef = useRef([]);
  const pendingCenterRef = useRef(null); // Store pending center updates

  // Load Google Maps script when map becomes visible
  useEffect(() => {
    if (!googleMapsApiKey) {
      setMapError("Google Maps API key is missing");
      return;
    }

    // Only load if map is visible AND not already loaded/loading
    if (!isMapVisible || mapLoaded || isLoading) return;

    let isMounted = true;
    setIsLoading(true);

    const loadMaps = async () => {
      try {
        await loadGoogleMapsScript(googleMapsApiKey);
        if (isMounted) {
          setMapLoaded(true);
          setMapError(null);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to load Google Maps:", error);
          setMapError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMaps();

    return () => {
      isMounted = false;
    };
  }, [googleMapsApiKey, isMapVisible, mapLoaded, isLoading]);

  // Clean up map instance on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current && window.google && window.google.maps) {
        window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      }
    };
  }, []);

  // CRITICAL OPTIMIZATION: Use stable center prop that doesn't change frequently
  // This prevents GoogleMap component from re-rendering/re-initializing
  const stableCenter = React.useMemo(() => {
    return mapCenter;
  }, [mapCenter.lat, mapCenter.lng]); // Only update when lat/lng actually change

  // Update map center without recreating the map
  useEffect(() => {
    if (!mapInstanceRef.current || !isMapVisible) return;
    
    // Use panTo for smooth animation instead of recreating the map
    mapInstanceRef.current.panTo(mapCenter);
  }, [mapCenter, isMapVisible]);

  // FIXED: Update map center based on activeSpace or filters - ONLY WHEN MAP IS VISIBLE
  useEffect(() => {
    // CRITICAL FIX: Don't update map center state when map is closed
    // This prevents unnecessary state changes when hovering over spaces with map closed
    if (!isMapVisible) {
      // Store the pending center update for when map opens
      if (activeSpace?.address) {
        pendingCenterRef.current = {
          lat: parseFloat(activeSpace.address.lat),
          lng: parseFloat(activeSpace.address.lng),
        };
      }
      return;
    }
    
    let newCenter = null;
    
    if (activeSpace && activeSpace.address) {
      newCenter = {
        lat: parseFloat(activeSpace.address.lat),
        lng: parseFloat(activeSpace.address.lng),
      };
    } else if (filters?.lat && filters?.lng) {
      newCenter = {
        lat: parseFloat(filters.lat),
        lng: parseFloat(filters.lng),
      };
    } else if (filters?.lat_p1 && filters?.lat_p2 && filters?.lng_p1 && filters?.lng_p2) {
      newCenter = {
        lat: (parseFloat(filters.lat_p1) + parseFloat(filters.lat_p2)) / 2,
        lng: (parseFloat(filters.lng_p1) + parseFloat(filters.lng_p2)) / 2,
      };
    } else {
      newCenter = defaultCenter;
    }
    
    // Only update if center actually changed to prevent unnecessary re-renders
    if (newCenter.lat !== mapCenter.lat || newCenter.lng !== mapCenter.lng) {
      setMapCenter(newCenter);
    }
  }, [isMapVisible, activeSpace, filters.lat, filters.lng, filters.lat_p1, filters.lat_p2, filters.lng_p1, filters.lng_p2]);

  // Apply pending center when map opens
  useEffect(() => {
    if (isMapVisible && pendingCenterRef.current) {
      setMapCenter(pendingCenterRef.current);
      pendingCenterRef.current = null;
    }
  }, [isMapVisible]);

  // Memoize markers calculation - only when map is visible and spaces actually change
  const markers = React.useMemo(() => {
    // CRITICAL: Don't calculate markers when map is closed
    if (!isMapVisible || !mapLoaded || mapError || !spaces.length) return [];

    // Check if spaces actually changed to avoid unnecessary recalculations
    if (prevSpacesRef.current.length === spaces.length) {
      // Quick check if any space changed
      const spacesChanged = spaces.some((space, index) => {
        const prev = prevSpacesRef.current[index];
        return !prev || 
               prev.id !== space.id || 
               prev.address?.lat !== space.address?.lat ||
               prev.address?.lng !== space.address?.lng;
      });
      
      if (!spacesChanged && prevMarkersRef.current.length) {
        return prevMarkersRef.current;
      }
    }

    // Update prev spaces ref
    prevSpacesRef.current = spaces;

    const offset = 0.0007;
    const positions = {};

    const calculatedMarkers = spaces
      .filter(
        (space) =>
          space.address &&
          !isNaN(parseFloat(space.address.lat)) &&
          !isNaN(parseFloat(space.address.lng))
      )
      .map((space) => {
        const lat = parseFloat(space.address.lat);
        const lng = parseFloat(space.address.lng);
        const key = `${lat}-${lng}`;

        positions[key] = (positions[key] || 0) + 1;

        return {
          id: space.id,
          lat: lat + offset * positions[key],
          lng: lng + offset * positions[key],
          price: getPriceForMinDuration(space, t),
          isActive: space.id === activeSpace?.id,
          details: {
            imageUrl: space.images && space.images[0]?.image_url,
            usage: space.placeUsage?.name,
            name: space.name,
            postalCode: space.address?.postal_code,
            city: space.address?.city,
          },
        };
      });

    // Store for next comparison
    prevMarkersRef.current = calculatedMarkers;
    return calculatedMarkers;
  }, [spaces, mapLoaded, mapError, isMapVisible, t, activeSpace?.id]);

  // Stable callbacks
  const handleMarkerHover = useCallback((markerId) => {
    setHoveredMarkerId(markerId);
  }, []);

  const handleMarkerLeave = useCallback(() => {
    setHoveredMarkerId(null);
  }, []);

  const handleMarkerClick = useCallback((markerId) => {
    navigate(`/space/${markerId}`);
  }, [navigate]);

  // Debounced map drag end handler to prevent multiple API calls
  const handleMapDragEnd = useCallback(
    debounce(async () => {
      if (!isSearchEnabled || !mapRef.current) return;

      const bounds = mapRef.current.getBounds();
      if (bounds) {
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();

        const latMin = southWest.lat();
        const latMax = northEast.lat();
        const lngMin = southWest.lng();
        const lngMax = northEast.lng();

        dispatch(
          setFilters({
            lat_p1: latMin,
            lat_p2: latMax,
            lng_p1: lngMin,
            lng_p2: lngMax,
          })
        );
      }
    }, 500), // Wait 500ms after dragging stops
    [isSearchEnabled, dispatch]
  );

  const handleMapLoad = useCallback((map) => {
    mapLoadCount++;
    mapRef.current = map;
    mapInstanceRef.current = map;
    
    // Log for monitoring (remove in production or send to analytics)
    console.log(`MAP INSTANCE CREATED - count: ${mapLoadCount} - should be minimal`);
  }, []);

  const handleMapClick = useCallback(() => {
    setIsMapVisible(false);
  }, [setIsMapVisible]);

  const handleCloseMap = useCallback(() => {
    setIsMapVisible(false);
  }, [setIsMapVisible]);

  const handleToggleSearch = useCallback(() => {
    setIsSearchEnabled((prev) => !prev);
  }, []);

  const handleShowMap = useCallback(() => {
    setIsMapVisible(true);
  }, [setIsMapVisible]);

  if (!googleMapsApiKey) {
    return (
      <div className={styles.map}>
        <div className={styles.errorContainer}>
          <p>Google Maps API key is missing. Please check your .env file.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.map}>
      <div
        className={`${styles.mapContainer} ${
          isMapVisible ? styles.visible : ""
        }`}
      >
        {isMapVisible ? (
          <>
            {isLoading && (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading map...</p>
              </div>
            )}
            
            {mapError && (
              <div className={styles.errorContainer}>
                <p>Failed to load map: {mapError}</p>
              </div>
            )}
            
            {mapLoaded && !mapError && (
              <GoogleMap
                onLoad={handleMapLoad}
                mapContainerStyle={containerStyle}
                center={stableCenter} // Use stable memoized center
                zoom={13}
                options={mapOptions}
                onClick={handleMapClick}
                onDragEnd={handleMapDragEnd}
              >
                <div className={styles.mapControls}>
                  <Checkbox
                    checked={isSearchEnabled}
                    greyText={true}
                    isSquare
                    onChange={handleToggleSearch}
                    label={t("search.map.searchOnMove")}
                  />
                </div>
                
                {markers.map((marker) => (
                  <MapMarker
                    key={marker.id}
                    marker={marker}
                    isActive={marker.id === activeSpace?.id}
                    isHovered={hoveredMarkerId === marker.id}
                    onHover={handleMarkerHover}
                    onLeave={handleMarkerLeave}
                    onClick={handleMarkerClick}
                  />
                ))}
                
                <div
                  onClick={handleCloseMap}
                  className={styles.closeMapButton}
                  aria-label="Close Map"
                >
                  <img src={ArrowRight} alt="Close map icon" />
                </div>
              </GoogleMap>
            )}
          </>
        ) : (
          <div className={styles.mapPlaceholder}>
            <button 
              onClick={handleShowMap}
              className={styles.showMapButton}
            >
              Show Map
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize the entire component
export default memo(SearchMap);